import React, { useState, useEffect } from 'react';
import { Descriptions, Table, message, Spin, Button } from 'antd';
import axios from 'axios';
import { useParams } from 'react-router-dom'; // For dynamic id in route
import { baseUrl } from '../../config/apiConfig';
import tokenStore from '../../stores/TokenStore';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const MerchantDetailPage = () => {
    const { id } = useParams(); // Get the merchant id from route parameters
    const [merchantData, setMerchantData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false); // Loading state for submit action
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        // Fetch merchant data from API
        axios.post(baseUrl + '/merchant/info/v2', { id })
            .then((response) => {
                if (response.data.code === 200) {
                    setMerchantData(response.data.data);
                } else {
                    message.error('Failed to fetch merchant data');
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching merchant data:', error);
                message.error('Error fetching merchant data');
                setLoading(false);
            });
    }, [id]);

    const handleSubmitApplication = () => {
        setSubmitting(true);
        axios.post(`${baseUrl}/merchant/submit_merchant_wechat`, { id }, {
            headers: {
                token: tokenStore.token
            }
        })
            .then((response) => {
                if (response.data.code === 200) {
                    message.success('Application submitted successfully!');
                } else {
                    message.error(response.data.message || 'Failed to submit application');
                }
            })
            .catch((error) => {
                console.error('Error submitting application:', error);
                message.error('Error submitting application');
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    if (loading) {
        return <Spin size="large" />;
    }

    if (!merchantData) {
        return <div>No merchant data available</div>;
    }

    const { tmerchant, tuser, tmerchantInfo, ttipList } = merchantData;

    // Columns for ttipList table
    const ttipColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (type === 2 ? 'Type 2' : 'Other'),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Descriptions title="Merchant Info" bordered column={2}>
                {Object.entries(tmerchant || {}).map(([key, value]) => (
                    <Descriptions.Item label={key} key={key}>
                        {value || 'N/A'}
                    </Descriptions.Item>
                ))}
            </Descriptions>

            <Descriptions title="User Info" bordered column={2} style={{ marginTop: '24px' }}>
                {Object.entries(tuser || {}).map(([key, value]) => (
                    <Descriptions.Item label={key} key={key}>
                        {value || 'N/A'}
                    </Descriptions.Item>
                ))}

                {merchantData.tuserList && merchantData.tuserList[0] && (
                    <>
                        <Descriptions.Item label="User Email">
                            {merchantData.tuserList[0].email || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="User Password">
                            <span style={{ cursor: 'pointer' }} onClick={() => setShowPassword(!showPassword)}>
                                {showPassword
                                    ? merchantData.tuserList[0].password
                                    : '••••••••••'}
                                &nbsp;
                                <Button
                                    type="link"
                                    size="small"
                                    icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                />
                            </span>
                        </Descriptions.Item>
                    </>
                )}
            </Descriptions>

            <Descriptions title="Merchant Additional Info" bordered column={2} style={{ marginTop: '24px' }}>
                {Object.entries(tmerchantInfo || {}).map(([key, value]) => (
                    <Descriptions.Item label={key} key={key}>
                        {value || 'N/A'}
                    </Descriptions.Item>
                ))}
            </Descriptions>

            <h4 style={{ marginTop: '24px' }}>Ttip List</h4>
            <Table
                columns={ttipColumns}
                dataSource={ttipList || []}
                rowKey="id"
                pagination={false}
            />

            {/* Conditional render button */}
            {tmerchant.applicationStatus === 'IN_PROCESS' && (
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button
                        type="primary"
                        loading={submitting}
                        onClick={handleSubmitApplication}
                    >
                        Submit Application(WeChat Pay)
                    </Button>
                </div>
            )}
        </div>
    );
};

export default MerchantDetailPage;
